import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { deriveSignalStripMetrics } from './tokenIntelligenceDerived';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
    gap: 10,
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 8,
    },
  },
  item: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'rgba(12, 18, 38, 0.85)',
    padding: '10px 12px',
    minHeight: 74,
    boxSizing: 'border-box',
  },
  label: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  value: {
    color: '#e5e7ff',
    fontSize: 15,
    fontWeight: 900,
    lineHeight: 1.2,
  },
  meta: {
    color: '#8d93c9',
    fontSize: 11,
    marginTop: 6,
    lineHeight: 1.35,
  },
  empty: {
    borderRadius: 12,
    border: '1px dashed rgba(77, 121, 255, 0.2)',
    background: 'rgba(12, 18, 38, 0.55)',
    padding: '12px 14px',
    color: '#9ea4d8',
    fontSize: 12,
    lineHeight: 1.45,
    gridColumn: '1 / -1',
  },
  tonePositive: { color: '#9ef7c5' },
  toneWarning: { color: '#ffcc80' },
  toneHot: { color: '#ff8a80' },
  toneNeutral: { color: '#cfd6ff' },
}));

function toneClass(classes, tone) {
  if (tone === 'positive') return classes.tonePositive;
  if (tone === 'warning') return classes.toneWarning;
  if (tone === 'hot') return classes.toneHot;
  return classes.toneNeutral;
}

export default function SignalStrip({ intelligence, metadata, liquidity, stories, priceHistory, theses, activity, loading, error, signals }) {
  const classes = useStyles();
  const resolvedSignals = Array.isArray(signals) && signals.length
    ? signals
    : intelligence && Array.isArray(intelligence.signals)
      ? intelligence.signals
      : deriveSignalStripMetrics({
          metadata,
          liquidity,
          stories,
          priceHistory,
          theses,
          activity,
        }, signals);

  if (loading) {
    return <Box className={classes.empty}>Loading signal strip…</Box>;
  }

  if (error && !resolvedSignals.length) {
    return <Box className={classes.empty}>{error}</Box>;
  }

  if (!resolvedSignals.length) {
    return <Box className={classes.empty}>No token signals available yet.</Box>;
  }

  return (
    <Box className={classes.root}>
      {resolvedSignals.map((signal, index) => {
        const key = signal && signal.label ? signal.label : `signal-${index}`;
        const value = signal && signal.value != null && String(signal.value).trim() ? String(signal.value) : '—';
        const meta = signal && signal.meta ? String(signal.meta) : '';
        const tone = signal && signal.tone ? String(signal.tone) : 'neutral';

        return (
          <Box key={key} className={classes.item}>
            <Typography className={classes.label}>{signal && signal.label ? signal.label : key}</Typography>
            <Typography className={`${classes.value} ${toneClass(classes, tone)}`}>
              {value}
            </Typography>
            <Typography className={classes.meta}>{meta || 'No additional context'}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}
